/**
 * Streaming result accumulation (PRD §6.3).
 *
 * 10^7 samples x Float64 is 80 MB *per output*, so retaining raw samples is not
 * the default. Everything reported is instead accumulated in constant memory:
 *
 *   - Welford moments through M4, for mean/sigma/skew/kurtosis and the
 *     normality test that gates Cpk.
 *   - A fixed-resolution histogram for the plot and for percentiles.
 *   - EXACT out-of-limit counters, kept separately from the histogram, so yield
 *     never inherits binning error. This matters: a bin straddling the USL
 *     would otherwise smear the very number the whole tool exists to report.
 *   - Centred sums of products for standardized regression coefficients.
 *   - A bounded prefix subsample, because rank correlation needs ranks and
 *     ranks need retained values.
 *
 * Accumulators merge pairwise, which is what lets shards run independently and
 * combine without affecting results.
 */

export interface OutputStats {
  count: number;
  mean: number;
  variance: number;
  stdDev: number;
  skewness: number;
  kurtosis: number;
  min: number;
  max: number;
  belowLsl: number;
  aboveUsl: number;
  inSpec: number;
}

const DEFAULT_BINS = 4096;

export interface OutputAccumulatorTransfer {
  lo: number;
  hi: number;
  binCount: number;
  lsl?: number;
  usl?: number;
  count: number;
  mean: number;
  m2: number;
  m3: number;
  m4: number;
  min: number;
  max: number;
  belowLsl: number;
  aboveUsl: number;
  underflow: number;
  overflow: number;
  bins: Float64Array;
}

export interface RunAccumulatorTransfer {
  outputs: OutputAccumulatorTransfer[];
  systemPass: number;
  total: number;
  nVars: number;
  centre: Float64Array;
  sx: Float64Array;
  sxx: Float64Array;
  sxy: Float64Array;
  sy: Float64Array;
  syy: Float64Array;
  subsampleLimit: number;
  subsampleCount: number;
  subX: Float64Array;
  subY: Float64Array;
}

export class OutputAccumulator {
  count = 0;
  private mean_ = 0;
  private m2 = 0;
  private m3 = 0;
  private m4 = 0;
  min = Infinity;
  max = -Infinity;

  /** Exact, binning-independent. */
  belowLsl = 0;
  aboveUsl = 0;

  readonly bins: Float64Array;
  underflow = 0;
  overflow = 0;

  constructor(
    readonly lo: number,
    readonly hi: number,
    readonly binCount: number = DEFAULT_BINS,
    readonly lsl?: number,
    readonly usl?: number,
  ) {
    this.bins = new Float64Array(binCount);
  }

  get binWidth(): number {
    return (this.hi - this.lo) / this.binCount;
  }

  push(x: number): void {
    const n1 = this.count;
    const n = n1 + 1;
    this.count = n;

    const delta = x - this.mean_;
    const deltaN = delta / n;
    const deltaN2 = deltaN * deltaN;
    const term1 = delta * deltaN * n1;

    this.mean_ += deltaN;
    this.m4 += term1 * deltaN2 * (n * n - 3 * n + 3) + 6 * deltaN2 * this.m2 - 4 * deltaN * this.m3;
    this.m3 += term1 * deltaN * (n - 2) - 3 * deltaN * this.m2;
    this.m2 += term1;

    if (x < this.min) this.min = x;
    if (x > this.max) this.max = x;

    if (this.lsl !== undefined && x < this.lsl) this.belowLsl++;
    if (this.usl !== undefined && x > this.usl) this.aboveUsl++;

    const idx = Math.floor((x - this.lo) / this.binWidth);
    if (idx < 0) this.underflow++;
    else if (idx >= this.binCount) this.overflow++;
    else this.bins[idx]++;
  }

  merge(other: OutputAccumulator): void {
    if (other.count === 0) return;
    if (this.count === 0) {
      this.copyFrom(other);
      return;
    }

    const nA = this.count;
    const nB = other.count;
    const n = nA + nB;
    const delta = other.mean_ - this.mean_;
    const delta2 = delta * delta;
    const delta3 = delta2 * delta;
    const delta4 = delta2 * delta2;

    const m4 =
      this.m4 +
      other.m4 +
      (delta4 * nA * nB * (nA * nA - nA * nB + nB * nB)) / (n * n * n) +
      (6 * delta2 * (nA * nA * other.m2 + nB * nB * this.m2)) / (n * n) +
      (4 * delta * (nA * other.m3 - nB * this.m3)) / n;

    const m3 =
      this.m3 +
      other.m3 +
      (delta3 * nA * nB * (nA - nB)) / (n * n) +
      (3 * delta * (nA * other.m2 - nB * this.m2)) / n;

    const m2 = this.m2 + other.m2 + (delta2 * nA * nB) / n;

    this.mean_ += (delta * nB) / n;
    this.m2 = m2;
    this.m3 = m3;
    this.m4 = m4;
    this.count = n;

    if (other.min < this.min) this.min = other.min;
    if (other.max > this.max) this.max = other.max;
    this.belowLsl += other.belowLsl;
    this.aboveUsl += other.aboveUsl;
    this.underflow += other.underflow;
    this.overflow += other.overflow;
    for (let i = 0; i < this.bins.length; i++) this.bins[i] += other.bins[i];
  }

  /**
   * Plain-object snapshot for structured cloning across a Worker boundary.
   *
   * Written by hand rather than inferred, because a class instance does not
   * survive postMessage and a silently dropped field here would show up as a
   * subtly wrong statistic rather than an error.
   */
  toTransfer(): OutputAccumulatorTransfer {
    return {
      lo: this.lo,
      hi: this.hi,
      binCount: this.binCount,
      lsl: this.lsl,
      usl: this.usl,
      count: this.count,
      mean: this.mean_,
      m2: this.m2,
      m3: this.m3,
      m4: this.m4,
      min: this.min,
      max: this.max,
      belowLsl: this.belowLsl,
      aboveUsl: this.aboveUsl,
      underflow: this.underflow,
      overflow: this.overflow,
      bins: this.bins,
    };
  }

  static fromTransfer(t: OutputAccumulatorTransfer): OutputAccumulator {
    const a = new OutputAccumulator(t.lo, t.hi, t.binCount, t.lsl, t.usl);
    a.count = t.count;
    a.mean_ = t.mean;
    a.m2 = t.m2;
    a.m3 = t.m3;
    a.m4 = t.m4;
    a.min = t.min;
    a.max = t.max;
    a.belowLsl = t.belowLsl;
    a.aboveUsl = t.aboveUsl;
    a.underflow = t.underflow;
    a.overflow = t.overflow;
    a.bins.set(t.bins);
    return a;
  }

  private copyFrom(o: OutputAccumulator): void {
    this.count = o.count;
    this.mean_ = o.mean_;
    this.m2 = o.m2;
    this.m3 = o.m3;
    this.m4 = o.m4;
    this.min = o.min;
    this.max = o.max;
    this.belowLsl = o.belowLsl;
    this.aboveUsl = o.aboveUsl;
    this.underflow = o.underflow;
    this.overflow = o.overflow;
    this.bins.set(o.bins);
  }

  stats(): OutputStats {
    const n = this.count;
    const variance = n > 1 ? this.m2 / (n - 1) : 0;
    const sd = Math.sqrt(variance);
    // Population moments for shape; the bias correction is negligible at the
    // sample sizes this tool operates at and its absence is easier to explain.
    const skewness = n > 2 && this.m2 > 0 ? (Math.sqrt(n) * this.m3) / Math.pow(this.m2, 1.5) : 0;
    const kurtosis = n > 3 && this.m2 > 0 ? (n * this.m4) / (this.m2 * this.m2) - 3 : 0;

    return {
      count: n,
      mean: this.mean_,
      variance,
      stdDev: sd,
      skewness,
      kurtosis,
      min: this.min,
      max: this.max,
      belowLsl: this.belowLsl,
      aboveUsl: this.aboveUsl,
      inSpec: n - this.belowLsl - this.aboveUsl,
    };
  }

  /**
   * Percentile by linear interpolation within the histogram.
   *
   * Approximate by construction — resolution is one bin width. Yield figures
   * never come from here; they come from the exact counters above.
   */
  percentile(p: number): number {
    const target = p * this.count;
    if (target <= this.underflow) return this.min;
    let cum = this.underflow;
    const w = this.binWidth;
    for (let i = 0; i < this.binCount; i++) {
      const c = this.bins[i];
      if (cum + c >= target && c > 0) {
        const frac = (target - cum) / c;
        return this.lo + (i + frac) * w;
      }
      cum += c;
    }
    return this.max;
  }
}

/** Accumulates everything a single Monte Carlo run reports. */
export class RunAccumulator {
  readonly outputs: OutputAccumulator[];
  /** Samples where every output with limits is simultaneously in spec. */
  systemPass = 0;
  total = 0;

  // Centred sums of products for standardized regression coefficients.
  // Centring on the nominal value (rather than 0) keeps the normal equations
  // well conditioned when variables have large offsets, e.g. a 100k resistor.
  private readonly nVars: number;
  private readonly nOuts: number;
  private readonly centre: Float64Array;
  private readonly sx: Float64Array;
  private readonly sxx: Float64Array;
  private readonly sxy: Float64Array;
  private readonly sy: Float64Array;
  private readonly syy: Float64Array;

  // Bounded prefix subsample for rank statistics.
  readonly subsampleLimit: number;
  subsampleCount = 0;
  readonly subX: Float64Array;
  readonly subY: Float64Array;

  constructor(
    outputs: OutputAccumulator[],
    nVars: number,
    centre: Float64Array,
    subsampleLimit = 65536,
  ) {
    this.outputs = outputs;
    this.nVars = nVars;
    this.nOuts = outputs.length;
    this.centre = centre.slice(0, nVars);
    this.sx = new Float64Array(nVars);
    this.sxx = new Float64Array(nVars * nVars);
    this.sxy = new Float64Array(nVars * this.nOuts);
    this.sy = new Float64Array(this.nOuts);
    this.syy = new Float64Array(this.nOuts);

    // Zero means "this range can never reach the retained prefix, so collect
    // nothing" — an explicit choice by the caller, not a value to clamp.
    if (subsampleLimit <= 0) {
      this.subsampleLimit = 0;
    } else {
      // Otherwise keep the subsample from consuming more than ~32 MB.
      const maxCells = 4_000_000;
      const perRow = nVars + this.nOuts;
      this.subsampleLimit = Math.max(
        1024,
        Math.min(subsampleLimit, Math.floor(maxCells / perRow)),
      );
    }
    this.subX = new Float64Array(this.subsampleLimit * nVars);
    this.subY = new Float64Array(this.subsampleLimit * this.nOuts);
  }

  /** `vars` is the variable prefix of the env vector; `outs` the output values. */
  push(vars: Float64Array, outs: Float64Array): void {
    this.total++;

    let allPass = true;
    for (let o = 0; o < this.nOuts; o++) {
      const acc = this.outputs[o];
      const y = outs[o];
      const before = acc.belowLsl + acc.aboveUsl;
      acc.push(y);
      if (acc.belowLsl + acc.aboveUsl > before) allPass = false;
    }
    if (allPass) this.systemPass++;

    const nv = this.nVars;
    for (let i = 0; i < nv; i++) {
      const xi = vars[i] - this.centre[i];
      this.sx[i] += xi;
      for (let j = i; j < nv; j++) {
        this.sxx[i * nv + j] += xi * (vars[j] - this.centre[j]);
      }
      for (let o = 0; o < this.nOuts; o++) {
        this.sxy[i * this.nOuts + o] += xi * outs[o];
      }
    }
    for (let o = 0; o < this.nOuts; o++) {
      this.sy[o] += outs[o];
      this.syy[o] += outs[o] * outs[o];
    }

    if (this.subsampleCount < this.subsampleLimit) {
      const k = this.subsampleCount++;
      this.subX.set(vars.subarray(0, nv), k * nv);
      for (let o = 0; o < this.nOuts; o++) this.subY[k * this.nOuts + o] = outs[o];
    }
  }

  merge(other: RunAccumulator): void {
    for (let o = 0; o < this.nOuts; o++) this.outputs[o].merge(other.outputs[o]);
    this.systemPass += other.systemPass;
    this.total += other.total;

    for (let i = 0; i < this.sx.length; i++) this.sx[i] += other.sx[i];
    for (let i = 0; i < this.sxx.length; i++) this.sxx[i] += other.sxx[i];
    for (let i = 0; i < this.sxy.length; i++) this.sxy[i] += other.sxy[i];
    for (let o = 0; o < this.nOuts; o++) {
      this.sy[o] += other.sy[o];
      this.syy[o] += other.syy[o];
    }

    // Shards are merged in iteration order, so the combined subsample stays a
    // deterministic prefix of the run regardless of completion order.
    const room = this.subsampleLimit - this.subsampleCount;
    const take = Math.min(room, other.subsampleCount);
    if (take > 0) {
      this.subX.set(
        other.subX.subarray(0, take * this.nVars),
        this.subsampleCount * this.nVars,
      );
      this.subY.set(other.subY.subarray(0, take * this.nOuts), this.subsampleCount * this.nOuts);
      this.subsampleCount += take;
    }
  }

  /**
   * Snapshot for structured cloning. Only the populated prefix of the
   * subsample is copied — a chunk past the prefix limit carries none at all,
   * which is what keeps the transfer small in a parallel run.
   */
  toTransfer(): RunAccumulatorTransfer {
    return {
      outputs: this.outputs.map((o) => o.toTransfer()),
      systemPass: this.systemPass,
      total: this.total,
      nVars: this.nVars,
      centre: this.centre,
      sx: this.sx,
      sxx: this.sxx,
      sxy: this.sxy,
      sy: this.sy,
      syy: this.syy,
      subsampleLimit: this.subsampleLimit,
      subsampleCount: this.subsampleCount,
      subX: this.subX.slice(0, this.subsampleCount * this.nVars),
      subY: this.subY.slice(0, this.subsampleCount * this.nOuts),
    };
  }

  static fromTransfer(t: RunAccumulatorTransfer): RunAccumulator {
    const outputs = t.outputs.map(OutputAccumulator.fromTransfer);
    const r = new RunAccumulator(outputs, t.nVars, t.centre, t.subsampleLimit);
    r.systemPass = t.systemPass;
    r.total = t.total;
    r.sx.set(t.sx);
    r.sxx.set(t.sxx);
    r.sxy.set(t.sxy);
    r.sy.set(t.sy);
    r.syy.set(t.syy);
    r.subsampleCount = t.subsampleCount;
    r.subX.set(t.subX);
    r.subY.set(t.subY);
    return r;
  }

  /** Covariance matrix entry (i,j) over the variables. */
  covXX(i: number, j: number): number {
    const nv = this.nVars;
    const [a, b] = i <= j ? [i, j] : [j, i];
    const n = this.total;
    return (this.sxx[a * nv + b] - (this.sx[a] * this.sx[b]) / n) / (n - 1);
  }

  /** Covariance between variable i and output o. */
  covXY(i: number, o: number): number {
    const n = this.total;
    return (this.sxy[i * this.nOuts + o] - (this.sx[i] * this.sy[o]) / n) / (n - 1);
  }

  varY(o: number): number {
    const n = this.total;
    return (this.syy[o] - (this.sy[o] * this.sy[o]) / n) / (n - 1);
  }
}
